import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery } from "@apollo/client/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import themestyle from "../theme";
import AddPassForm from "../components/AddPassForm";
import AllPassesTable from "../components/AllPassesTable";
import ConfirmDialog from "../components/confirmdialogbox";
import CustomSnackbar from "../components/Snackbar";
import { DELETE_PASS } from "../graphql/mutation";
import { ME } from "../graphql/query";
import UserPassesCardView from "../components/UserPassesCardView";

const PAGE_TITLE = "Passes - United Grooves";

type ViewType =
  | "ADD_PASS"
  | "ALL_PASSES"
  | "VIEW_PASS"
  | "EDIT_PASS";

const PassesManagement: React.FC = () => {
  const navigate = useNavigate();
  const { mode, id } = useParams<{ mode?: string; id?: string }>();
  const location = useLocation();
  const [refreshCount, setRefreshCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passToDelete, setPassToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error" | "info">("success");

  const getActiveView = (): ViewType => {
    if (location.pathname === "/passes/add") return "ADD_PASS";
    if (mode === "view" && id) return "VIEW_PASS";
    if (mode === "edit" && id) return "EDIT_PASS";
    return "ALL_PASSES";
  };

  const activeView = getActiveView();
  const selectedPassId = id;

  const { data: meData } = useQuery(ME, {
    fetchPolicy: "cache-first",
  });
  
  const storedUser = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);
  const userRole = meData?.me?.role?.toLowerCase() || storedUser?.role?.toLowerCase() || "";
  const isUserRole = userRole === "user";

  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentStatus = params.get("paymentStatus");
    const paymentMessage = params.get("paymentMessage");
    if (paymentStatus && paymentMessage) {
      setSnackbarMessage(paymentMessage);
      setSnackbarType(paymentStatus === "success" ? "success" : "error");
      setSnackbarOpen(true);
      if (paymentStatus === "success") {
        setRefreshCount((prev) => prev + 1);
      }
      navigate("/passes", { replace: true });
    }
  }, [location.search, navigate]);

  const [deletePass] = useMutation(DELETE_PASS);

  const handleView = (id: string) => {
    navigate(`/passes/view/${id}`);
  };

  const handleReport = (id: string) => {
    navigate(`/passes/report/${id}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/passes/edit/${id}`);
  };

  const handleBack = () => {
    navigate("/passes");
  };

  const handleSuccess = () => {
    navigate("/passes");
    setRefreshCount((prev) => prev + 1);
  };

  const handleDeleteClick = (id: string, name: string) => {
    setPassToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!passToDelete) return;
    try {
      const { data: response }: any = await deletePass({
        variables: { id: passToDelete.id },
      });
      if (response?.deletePass?.success) {
        setSnackbarMessage(response.deletePass.message || "Pass deleted");
        setSnackbarType("error");
        setSnackbarOpen(true);
        handleSuccess();
      } else {
        setSnackbarMessage(
          response?.deletePass?.message || "Failed to delete pass"
        );
        setSnackbarType("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setDeleteDialogOpen(false);
      setPassToDelete(null);
    }
  };

  const isAllView = activeView === "ALL_PASSES";
  const isAddView = activeView === "ADD_PASS";

  return (
    <Box sx={{ mt: { xs: 1, sm: 3, md: 3 }, px: { xs: 1, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: { xs: 2, md: 0 },
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 0.5,
          }}
        >
          {(activeView === "VIEW_PASS" || activeView === "EDIT_PASS") && (
            <Box
              onClick={handleBack}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                color: "#6B7280",
                fontSize: "14px",
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />
              Back
            </Box>
          )}
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ fontSize: "24px" }}
            fontFamily={themestyle.fontFamily.Roboto}
          >
            {activeView === "VIEW_PASS"
              ? "View Pass"
              : activeView === "EDIT_PASS"
              ? "Edit Pass"
              : "Passes"}
          </Typography>
        </Box>

        {!isUserRole && (activeView === "ADD_PASS" || activeView === "ALL_PASSES") && (
          <Box
            sx={{
              display: "flex",
              backgroundColor: themestyle.colors.white,
              borderRadius: "6px",
              padding: "6px",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
              width: { xs: "100%", md: "fit-content" },
              justifyContent: { xs: "space-between", md: "flex-start" },
            }}
          >
            <Box
              onClick={() => {
                navigate("/passes");
              }}
              sx={{
                cursor: "pointer",
                flex: 1,
                textAlign: "center",
                px: { xs: 2, sm: 4 },
                py: { xs: 1.5, sm: 1.5 },
                fontSize: { xs: "14px", sm: "16px" },
                borderRadius: "6px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: isAllView ? "#4CAF50" : "#6B7280",
                backgroundColor: isAllView ? "#EEF7E9" : "transparent",
                transition: "all 0.3s ease",
              }}
            >
              All Passes
            </Box>
            <Box
              onClick={() => {
                navigate("/passes/add");
              }}
              sx={{
                cursor: "pointer",
                flex: 1,
                textAlign: "center",
                px: { xs: 2, sm: 4 },
                py: { xs: 1.5, sm: 1.5 },
                fontSize: { xs: "14px", sm: "16px" },
                borderRadius: "6px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: isAddView ? "#4CAF50" : "#6B7280",
                backgroundColor: isAddView ? "#EEF7E9" : "transparent",
                transition: "all 0.3s ease",
              }}
            >
              Add Passes
            </Box>
          </Box>
        )}
      </Box>

      {activeView === "ADD_PASS" && (
        <AddPassForm
          onSuccess={handleSuccess}
          onCancel={() => navigate("/passes")}
        />
      )}

      {activeView === "ALL_PASSES" && (
        isUserRole ? (
          <UserPassesCardView
            onView={handleView}
            refreshTrigger={refreshCount}
            initialTab={(location.state as any)?.initialTab}
            bookingContext={(location.state as any)?.bookingContext ?? null}
          />
        ) : (
          <AllPassesTable
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            onReport={handleReport}
            refreshTrigger={refreshCount}
          />
        )
      )}

      {activeView === "VIEW_PASS" && selectedPassId && (
        <AddPassForm
          mode="view"
          id={selectedPassId}
          onCancel={handleBack}
          onEdit={() => navigate(`/passes/edit/${selectedPassId}`)}
          onDelete={handleDeleteClick}
        />
      )}

      {activeView === "EDIT_PASS" && selectedPassId && (
        <AddPassForm
          mode="edit"
          id={selectedPassId}
          onSuccess={handleSuccess}
          onCancel={handleBack}
          onDelete={handleDeleteClick}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        icon={<DeleteOutlineIcon sx={{ color: "#EB2857" }} />}
        message={`Are you sure you want to delete ${passToDelete?.name || "this pass"}?`}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteDialogOpen(false)}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="danger"
        iconBgColor="danger"
      />

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default PassesManagement;
